import { useAuth } from "../hooks/useAuth";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Lock, Unlock, Copy, Check } from "lucide-react";
import GdprTab from "../components/GdprTab";

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  createdAt: string;
}

interface Tenant {
  id: string;
  name: string;
  domain?: string;
  slug: string;
}

interface EditingUser extends User {
  editingName?: string;
  editingEmail?: string;
}

export default function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newOperatore, setNewOperatore] = useState({ email: "", nome: "" });
  const [creatingOperatore, setCreatingOperatore] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingEmail, setEditingEmail] = useState("");
  const [copied, setCopied] = useState(false);

  if (user?.role !== "admin") {
    return <Navigate to="/dashboard/overview" replace />;
  }

  useEffect(() => {
    fetchUsers();
    fetchTenant();
  }, []);

  const fetchTenant = async () => {
    try {
      const res = await axios.get(`/platform/tenant/${user?.tenantId}`);
      setTenant(res.data.data);
    } catch (err) {
      console.error("Failed to load tenant info");
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get("/admin/users");
      setUsers(res.data.data || []);
    } catch (err) {
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers or non-HTTPS contexts
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const generateRegistrationLink = (): string => {
    if (!user?.tenantId) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/visitor-registration?tenantId=${user.tenantId}&source=nfc`;
  };

  const createOperatore = async () => {
    if (!newOperatore.email || !newOperatore.nome) {
      alert("Email and name required");
      return;
    }

    setCreatingOperatore(true);
    try {
      const res = await axios.post(`/admin/tenants/${user?.tenantId}/operatori`, {
        email: newOperatore.email,
        nome: newOperatore.nome,
      });

      if (res.data.data?.operatore) {
        setUsers([res.data.data.operatore, ...users]);
      }

      setNewOperatore({ email: "", nome: "" });
      alert("Operatore created successfully. Invitation email sent.");
    } catch (err: any) {
      alert(
        err.response?.data?.error?.message || "Failed to create operatore"
      );
    } finally {
      setCreatingOperatore(false);
    }
  };

  const startEditing = (u: User) => {
    setEditingId(u.id);
    setEditingName(u.name || "");
    setEditingEmail(u.email);
  };

  const saveEdit = async () => {
    if (!editingId) return;

    try {
      const res = await axios.put(`/admin/users/${editingId}`, {
        name: editingName,
        email: editingEmail,
      });

      setUsers(users.map((u) => (u.id === editingId ? res.data.data : u)));
      setEditingId(null);
      alert("User updated successfully");
    } catch (err: any) {
      alert(err.response?.data?.error?.message || "Failed to update user");
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
    setEditingEmail("");
  };

  const suspendUser = async (userId: string, userEmail: string, currentStatus: string) => {
    const newStatus = currentStatus === "active" ? "suspended" : "active";
    const action = newStatus === "suspended" ? "suspend" : "reactivate";

    if (!confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} user ${userEmail}?`)) return;

    try {
      const res = await axios.put(`/admin/users/${userId}/status`, {
        status: newStatus,
      });

      setUsers(users.map((u) => (u.id === userId ? res.data.data : u)));
      alert(`User ${action}ed successfully`);
    } catch (err: any) {
      alert(err.response?.data?.error?.message || `Failed to ${action} user`);
    }
  };

  const deleteUser = async (userId: string, userEmail: string) => {
    if (!confirm(`Delete user ${userEmail}? This cannot be undone.`)) return;

    try {
      await axios.delete(`/admin/users/${userId}`);
      setUsers(users.filter((u) => u.id !== userId));
      alert("User deleted successfully");
    } catch (err: any) {
      alert(err.response?.data?.error?.message || "Failed to delete user");
    }
  };

  return (
    <div className="px-6 py-6 font-sans">
      <h1 className="text-3xl font-bold text-slate-900 mb-5">Admin Panel</h1>

      {/* Visitor Registration Link Section */}
      <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 mb-2">
          Visitor Registration Link
        </h2>
        <p className="text-slate-600 text-sm mb-4">
          Share this link with visitors or embed in QR codes for NFC tag registrations
        </p>
        {generateRegistrationLink() ? (
          <div className="flex items-center gap-2">
            <div className="flex-1 px-3 py-2 bg-white rounded-lg border border-green-200 font-mono text-sm text-slate-700 truncate">
              {generateRegistrationLink()}
            </div>
            <button
              onClick={() => copyToClipboard(generateRegistrationLink())}
              className={`px-4 py-2 rounded-lg border-none flex items-center gap-1.5 text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                copied
                  ? 'bg-green-600 text-white'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {copied ? (
                <>
                  <Check size={14} />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={14} />
                  Copy
                </>
              )}
            </button>
          </div>
        ) : (
          <p className="text-slate-500 text-sm">Loading tenant information...</p>
        )}
      </div>

      {/* Create Operatore Section */}
      <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm mb-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          Create New Operatore
        </h2>
        <div className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Email
            </label>
            <input
              type="email"
              placeholder="operatore@museum.local"
              value={newOperatore.email}
              onChange={(e) =>
                setNewOperatore({ ...newOperatore, email: e.target.value })
              }
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm box-border"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Full Name
            </label>
            <input
              type="text"
              placeholder="John Doe"
              value={newOperatore.nome}
              onChange={(e) =>
                setNewOperatore({ ...newOperatore, nome: e.target.value })
              }
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm box-border"
            />
          </div>
          <button
            onClick={createOperatore}
            disabled={creatingOperatore}
            className={`px-4 py-2 rounded-lg border-none flex items-center gap-1.5 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              creatingOperatore
                ? 'bg-slate-300 text-white cursor-not-allowed'
                : 'bg-blue-700 text-white hover:bg-blue-800'
            }`}
          >
            <Plus size={14} />
            {creatingOperatore ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>

      {/* Users Section */}
      <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          Team Members ({users.length})
        </h2>

        {loading && <p className="text-slate-500">Loading...</p>}
        {error && <p className="text-red-600">{error}</p>}

        {!loading && users.length > 0 && (
          <table className="w-full border-collapse text-sm text-slate-900">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 font-semibold">Email</th>
                <th className="text-left py-2 font-semibold">Name</th>
                <th className="text-left py-2 font-semibold">Role</th>
                <th className="text-left py-2 font-semibold">Status</th>
                <th className="text-left py-2 font-semibold">Joined</th>
                <th className="text-left py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isCurrentUser = u.id === user?.id;
                const isEditing = editingId === u.id;

                return (
                  <tr key={u.id} className="border-b border-slate-100">
                    <td className="py-3">
                      {isEditing ? (
                        <input
                          type="email"
                          value={editingEmail}
                          onChange={(e) => setEditingEmail(e.target.value)}
                          className="px-2 py-1 rounded border border-slate-300 text-xs w-full"
                        />
                      ) : (
                        u.email
                      )}
                    </td>
                    <td className="py-3">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="px-2 py-1 rounded border border-slate-300 text-xs w-full"
                        />
                      ) : (
                        u.name || "-"
                      )}
                    </td>
                    <td className="py-3">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                          u.role === "admin"
                            ? "bg-red-100 text-red-900"
                            : u.role === "operatore"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-purple-100 text-purple-700"
                        }`}
                      >
                        {u.role}
                      </span>
                    </td>
                    <td className="py-3">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                          u.status === "active"
                            ? "bg-green-200 text-green-900"
                            : "bg-amber-100 text-amber-900"
                        }`}
                      >
                        {u.status}
                      </span>
                    </td>
                    <td className="py-3">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      <div className="flex gap-1.5 flex-wrap">
                        {isEditing ? (
                          <>
                            <button
                              onClick={saveEdit}
                              className="px-2 py-1 rounded border border-green-300 bg-green-50 text-green-600 hover:bg-green-100 text-xs font-medium cursor-pointer transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="px-2 py-1 rounded border border-gray-300 bg-gray-50 text-gray-600 hover:bg-gray-100 text-xs font-medium cursor-pointer transition-colors"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEditing(u)}
                              disabled={isCurrentUser}
                              title={isCurrentUser ? "Cannot edit your own account" : "Edit user"}
                              className={`px-2 py-1 rounded border border-blue-300 flex items-center gap-0.5 text-xs font-medium transition-colors ${
                                isCurrentUser
                                  ? "bg-blue-50 text-blue-400 cursor-not-allowed opacity-50"
                                  : "bg-blue-50 text-blue-600 hover:bg-blue-100 cursor-pointer"
                              }`}
                            >
                              <Edit2 size={12} />
                              Edit
                            </button>
                            <button
                              onClick={() =>
                                suspendUser(u.id, u.email, u.status)
                              }
                              disabled={isCurrentUser || u.role === "admin"}
                              title={
                                isCurrentUser
                                  ? "Cannot suspend your own account"
                                  : u.role === "admin"
                                  ? "Cannot suspend admin users"
                                  : "Suspend/Reactivate user"
                              }
                              className={`px-2 py-1 rounded border flex items-center gap-0.5 text-xs font-medium transition-colors ${
                                isCurrentUser || u.role === "admin"
                                  ? "border-amber-300 bg-amber-50 text-amber-400 cursor-not-allowed opacity-50"
                                  : u.status === "suspended"
                                  ? "border-green-300 bg-green-50 text-green-600 hover:bg-green-100 cursor-pointer"
                                  : "border-amber-300 bg-amber-50 text-amber-600 hover:bg-amber-100 cursor-pointer"
                              }`}
                            >
                              {u.status === "suspended" ? (
                                <>
                                  <Unlock size={12} />
                                  Activate
                                </>
                              ) : (
                                <>
                                  <Lock size={12} />
                                  Suspend
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => deleteUser(u.id, u.email)}
                              disabled={isCurrentUser || u.role === "admin"}
                              title={
                                isCurrentUser
                                  ? "Cannot delete your own account"
                                  : u.role === "admin"
                                  ? "Cannot delete admin users"
                                  : "Delete user"
                              }
                              className={`px-2 py-1 rounded border border-red-300 flex items-center gap-0.5 text-xs font-medium transition-colors ${
                                isCurrentUser || u.role === "admin"
                                  ? "bg-red-50 text-red-400 cursor-not-allowed opacity-50"
                                  : "bg-red-50 text-red-600 hover:bg-red-100 cursor-pointer"
                              }`}
                            >
                              <Trash2 size={12} />
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!loading && users.length === 0 && (
          <p className="text-slate-400">No users found</p>
        )}
      </div>

      {/* GDPR Section */}
      <div className="mt-6 bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
        <GdprTab tenantId={user?.tenantId} isSuperAdmin={false} />
      </div>
    </div>
  );
}
